import type { Rule } from 'antd/es/form';
import { Decimal } from 'decimal.js';

/**
 * `allowNegative` opts OUT of the sign rule entirely, for amount fields whose
 * domain is genuinely signed (a register's "Financial impact" or "Variance").
 * Default behaviour is unchanged for every existing caller: money fields stay
 * non-negative unless they explicitly ask otherwise. Ignored when `gtZero` is
 * set, which is a strictly stronger constraint.
 */
export function buildCurrencyInputRules(options?: {
    isRequired?: boolean;
    gtZero?: boolean;
    allowNegative?: boolean;
    max?: Decimal;
    /**
     * Field-specific rules appended AFTER the built-in currency rules — use this
     * instead of spreading the result into a hand-built array, so the
     * currency-input lint plugin can keep proving the built-ins are applied.
     */
    extraRules?: Rule[];
}): Rule[] {
    return [
        {
            // Only for asterisk on UI, actual validation for isRequired happens in validator below
            ...(options?.isRequired ? { required: true } : {}),
            async validator(_, value: Decimal) {
                // `null` signals an invalid input (e.g. unparseable text from CurrencyInput) and
                // is always rejected as malformed. `undefined` means the field is empty: rejected
                // with a "required" message when isRequired, otherwise left valid.
                if (value === null) {
                    throw new Error('Invalid amount');
                }

                if (options?.isRequired && !Decimal.isDecimal(value)) {
                    throw new Error('Amount is required');
                }
            },
        },
        ...(options?.gtZero
            ? ([
                  {
                      async validator(_, value: Decimal) {
                          if (value?.lte(0)) {
                              throw new Error('Amount must be greater than $0');
                          }
                      },
                  },
              ] satisfies Rule[])
            : options?.allowNegative
              ? []
              : ([
                    {
                        async validator(_, value: Decimal) {
                            if (value?.lt(0)) {
                                throw new Error('Amount must be $0 or greater');
                            }
                        },
                    },
                ] satisfies Rule[])),
        ...(options?.max !== undefined
            ? ([
                  {
                      async validator(_, value: Decimal) {
                          if (options?.max === undefined) {
                              return;
                          }

                          if (value?.gt(options.max)) {
                              throw new Error(`Amount must be $${options.max.toString()} or less`);
                          }
                      },
                  },
              ] satisfies Rule[])
            : []),
        ...(options?.extraRules ?? []),
    ];
}
